function toDateKey(value) {
  if (!value) return null;
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
}

function dateKeyToUtcMs(value) {
  const dateKey = toDateKey(value);
  const match = String(dateKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function daysBetween(startDateKey, endDateKey) {
  const startMs = dateKeyToUtcMs(startDateKey);
  const endMs = dateKeyToUtcMs(endDateKey);
  if (startMs == null || endMs == null) return null;
  return Math.round((endMs - startMs) / (24 * 60 * 60 * 1000));
}

function normalizeText(value) {
  const text = String(value || "").trim();
  return text || null;
}

function normalizeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function extractCampaignWindow(value) {
  if (!value || typeof value !== "object") return null;

  const nestedWindow =
    value.flightWindow ||
    value.flight ||
    value.campaignWindow ||
    value.campaignFlightWindow ||
    null;
  if (nestedWindow && nestedWindow !== value) {
    const nested = extractCampaignWindow(nestedWindow);
    if (nested?.start || nested?.end) return nested;
  }

  const start = toDateKey(
    value.start ||
      value.startDate ||
      value.flightStart ||
      value.flight_start_date ||
      value.flightStartDate ||
      value.dateStart
  );
  const end = toDateKey(
    value.end ||
      value.endDate ||
      value.flightEnd ||
      value.flight_end_date ||
      value.flightEndDate ||
      value.dateEnd
  );

  return start || end ? { start, end } : null;
}

export function resolveDashboardCampaignWindowForEden(summary = null, selectedAdvertiserRecord = null) {
  const candidates = [];
  const addCandidate = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(addCandidate);
      return;
    }
    candidates.push(value);
  };

  addCandidate(summary?.canonicalSummary?.flightWindow);
  addCandidate(summary?.canonicalReporting?.flightWindow);
  addCandidate(summary?.campaign?.flightWindow);
  addCandidate(summary?.campaign);
  addCandidate(summary?.activeCampaign);
  addCandidate(summary?.currentCampaign);
  addCandidate(summary?.selectedCampaign);
  addCandidate(summary?.campaigns);
  addCandidate(summary?.runningCampaigns);
  addCandidate(summary?.activeCampaigns);
  addCandidate(summary?.canonicalSummary?.campaignScope?.activeCampaigns);
  addCandidate(summary?.canonicalReporting?.campaignScope?.activeCampaigns);
  addCandidate(summary?.metrics?.flightWindow);
  addCandidate(summary?.metrics?.campaignFlightWindow);
  addCandidate(selectedAdvertiserRecord?.campaign);
  addCandidate(selectedAdvertiserRecord?.activeCampaign);
  addCandidate(selectedAdvertiserRecord?.campaignFlightWindow);
  addCandidate(selectedAdvertiserRecord?.flightWindow);

  const windows = candidates
    .map(extractCampaignWindow)
    .filter((window) => window?.start && window?.end);

  if (!windows.length) return null;

  const starts = windows.map((window) => window.start).sort();
  const ends = windows.map((window) => window.end).sort();
  return {
    start: starts[0],
    end: ends[ends.length - 1],
  };
}

function resolvePrimaryCampaign(summary = null, selectedAdvertiserRecord = null) {
  const singletonCandidates = [
    summary?.campaign,
    summary?.activeCampaign,
    summary?.currentCampaign,
    summary?.selectedCampaign,
    selectedAdvertiserRecord?.campaign,
    selectedAdvertiserRecord?.activeCampaign,
  ].filter((candidate) => candidate && typeof candidate === "object");

  const namedSingleton = singletonCandidates.find((candidate) =>
    normalizeText(
      candidate.campaign_name ||
        candidate.campaignName ||
        candidate.name ||
        candidate.title
    )
  );
  if (namedSingleton) return namedSingleton;

  const arrayCandidates = [
    summary?.canonicalSummary?.campaignScope?.activeCampaigns,
    summary?.canonicalReporting?.campaignScope?.activeCampaigns,
    summary?.runningCampaigns,
    summary?.activeCampaigns,
    summary?.campaigns,
  ].filter(Array.isArray);

  for (const group of arrayCandidates) {
    const named = group.filter((candidate) =>
      normalizeText(
        candidate?.campaign_name ||
          candidate?.campaignName ||
          candidate?.name ||
          candidate?.title
      )
    );
    if (named.length === 1) return named[0];
  }

  return null;
}

function normalizeDashboardWeatherForEden(weather = null) {
  if (!weather || typeof weather !== "object") return null;

  const isLive = weather.isLive === true;
  const condition = normalizeText(weather.condition);
  const locationLabel = normalizeText(weather.locationLabel);
  const updatedAt = normalizeText(weather.updatedAt);
  const temperature = normalizeNumber(weather.temperature);
  const temperatureUnit = normalizeText(weather.temperatureUnit);
  const explicitlyUnavailable =
    weather.isLive === false &&
    Boolean(
      weather.error ||
        /unavailable/i.test(String(condition || "")) ||
        /unavailable/i.test(String(locationLabel || ""))
    );

  if (!isLive && !explicitlyUnavailable) {
    return null;
  }

  return {
    temperature: isLive ? temperature : null,
    temperatureUnit: isLive ? temperatureUnit || null : temperatureUnit || null,
    condition: condition || null,
    locationLabel: locationLabel || null,
    isLive,
    updatedAt: updatedAt || null,
  };
}

export function buildDashboardCampaignTimingContext({
  summary = null,
  selectedAdvertiserRecord = null,
  campaignDays = null,
  todayDateKey = null,
} = {}) {
  const currentDate = toDateKey(todayDateKey);
  const window = resolveDashboardCampaignWindowForEden(summary, selectedAdvertiserRecord);
  const primaryCampaign = resolvePrimaryCampaign(summary, selectedAdvertiserRecord);
  const campaignName = normalizeText(
    primaryCampaign?.campaign_name ||
      primaryCampaign?.campaignName ||
      primaryCampaign?.name ||
      primaryCampaign?.title
  );
  const status = normalizeText(
    primaryCampaign?.status ||
      primaryCampaign?.contract_status ||
      primaryCampaign?.campaign_status
  );
  const explicitPhase = normalizeText(primaryCampaign?.phase || primaryCampaign?.campaign_phase);

  if (!window?.start && !window?.end && !campaignName && !status && !explicitPhase) {
    return null;
  }

  const totalDaysRaw =
    window?.start && window?.end ? daysBetween(window.start, window.end) : null;
  const totalDays = totalDaysRaw != null ? Math.max(1, totalDaysRaw + 1) : null;
  const startsInRaw =
    currentDate && window?.start ? daysBetween(currentDate, window.start) : null;
  const startsIn = startsInRaw != null ? Math.max(0, startsInRaw) : null;
  const daysLeftFromWidget =
    campaignDays?.state === "active" || campaignDays?.state === "complete"
      ? normalizeNumber(campaignDays?.value)
      : null;
  const daysLeft =
    campaignDays?.state === "active"
      ? daysLeftFromWidget
      : campaignDays?.state === "complete"
      ? 0
      : null;

  let daysElapsed = null;
  if (currentDate && window?.start && window?.end && totalDays != null) {
    if (startsInRaw != null && startsInRaw > 0) {
      daysElapsed = 0;
    } else {
      const elapsedRaw = daysBetween(window.start, currentDate);
      if (elapsedRaw != null) {
        daysElapsed = Math.max(0, Math.min(totalDays, elapsedRaw + 1));
      }
    }
  }

  const derivedPhase =
    explicitPhase ||
    (campaignDays?.state === "preflight"
      ? "pre-flight"
      : campaignDays?.state === "complete"
      ? "post-flight"
      : campaignDays?.state === "active" && daysLeft != null && daysLeft <= 7
      ? "final-stretch"
      : campaignDays?.state === "active"
      ? "in-flight"
      : null);

  return {
    campaignName: campaignName || null,
    startDate: window?.start || null,
    endDate: window?.end || null,
    daysLeft,
    daysElapsed,
    totalDays,
    phase: derivedPhase || null,
    status: status || null,
    isActive: campaignDays?.state === "active",
    startsIn:
      campaignDays?.state === "preflight" && startsIn != null ? startsIn : null,
  };
}

export function buildEdenDashboardContext({
  summary = null,
  selectedAdvertiserRecord = null,
  weather = null,
  campaignDays = null,
  todayDateKey = null,
} = {}) {
  const currentDate = toDateKey(todayDateKey);
  const localWeather = normalizeDashboardWeatherForEden(weather);
  const campaignTiming = buildDashboardCampaignTimingContext({
    summary,
    selectedAdvertiserRecord,
    campaignDays,
    todayDateKey: currentDate,
  });

  const dashboardContext = {
    currentDate: currentDate || null,
    localWeather,
    campaignTiming,
  };

  return dashboardContext.currentDate || dashboardContext.localWeather || dashboardContext.campaignTiming
    ? dashboardContext
    : null;
}

export function resolveEdenDashboardContextFromPageAwareness(pageAwareness = null, edenContext = null) {
  const pageDashboardContext =
    pageAwareness?.dashboardContext && typeof pageAwareness.dashboardContext === "object"
      ? pageAwareness.dashboardContext
      : pageAwareness?.metadata?.dashboardContext &&
        typeof pageAwareness.metadata.dashboardContext === "object"
      ? pageAwareness.metadata.dashboardContext
      : null;

  if (pageDashboardContext) return pageDashboardContext;
  if (edenContext?.dashboardContext && typeof edenContext.dashboardContext === "object") {
    return edenContext.dashboardContext;
  }
  return null;
}
